import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

/**
 * Full-screen photo gallery lightbox.
 * Usage: <PhotoGallery images={[url1, url2]} initialIndex={0} open={true} onClose={() => {}} />
 */
const PhotoGallery = ({ images = [], initialIndex = 0, open, onClose }) => {
  const [current, setCurrent] = useState(initialIndex);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => { setCurrent(initialIndex); setZoomed(false); }, [initialIndex, open]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setCurrent(c => (c === 0 ? images.length - 1 : c - 1));
      if (e.key === "ArrowRight") setCurrent(c => (c === images.length - 1 ? 0 : c + 1));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, images.length, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const prev = useCallback(() => { setCurrent(c => (c === 0 ? images.length - 1 : c - 1)); setZoomed(false); }, [images.length]);
  const next = useCallback(() => { setCurrent(c => (c === images.length - 1 ? 0 : c + 1)); setZoomed(false); }, [images.length]);

  if (!open || !images.length) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col" data-testid="photo-gallery-lightbox">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 text-white/80">
        <span className="text-sm font-medium">{current + 1} / {images.length}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => setZoomed(z => !z)} className="p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Toggle zoom" data-testid="gallery-zoom">
            {zoomed ? <ZoomOut className="w-5 h-5" /> : <ZoomIn className="w-5 h-5" />}
          </button>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Close gallery" data-testid="gallery-close">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Image area */}
      <div className="flex-1 flex items-center justify-center relative px-4 min-h-0" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        {images.length > 1 && (
          <button onClick={prev} className="absolute left-4 md:left-8 z-10 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-all" aria-label="Previous" data-testid="gallery-prev">
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        <img
          src={images[current]}
          alt={`Photo ${current + 1}`}
          className={`max-h-full transition-transform duration-300 rounded-lg ${zoomed ? "max-w-none scale-150 cursor-zoom-out" : "max-w-full object-contain cursor-zoom-in"}`}
          onClick={() => setZoomed(z => !z)}
          draggable={false}
          data-testid="gallery-image"
        />

        {images.length > 1 && (
          <button onClick={next} className="absolute right-4 md:right-8 z-10 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-all" aria-label="Next" data-testid="gallery-next">
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="py-3 px-4 flex justify-center gap-2 overflow-x-auto">
          {images.map((img, idx) => (
            <button key={idx} onClick={() => { setCurrent(idx); setZoomed(false); }}
              className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${idx === current ? "border-white opacity-100" : "border-transparent opacity-50 hover:opacity-75"}`}
              data-testid={`gallery-thumb-${idx}`}>
              <img src={img} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;
